import { useState } from 'react';
import { submitContact } from '@/lib/api';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <div className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00A5FF] font-semibold mb-2">Contact</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#111] tracking-tight">Contactez-nous</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="border border-black/10 p-6">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-[#111] mb-1">Adresse</h3>
                  <p className="text-sm text-black/60">Unita Business</p>
                  <p className="text-sm text-black/60">7 Rambout Straat</p>
                  <p className="text-sm text-black/60">1930 Zaventem, Belgique</p>
                </div>
              </div>
            </div>
            <div className="border border-black/10 p-6">
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-[#111] mb-1">Téléphone</h3>
                  <a href="tel:+32489102365" className="text-sm text-black/60 hover:text-[#00A5FF]">0489 10 23 65</a>
                </div>
              </div>
            </div>
            <div className="border border-black/10 p-6">
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-[#00A5FF] mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-[#111] mb-1">Gérante</h3>
                  <p className="text-sm text-black/60">Désirée Malpica</p>
                  <a href="mailto:info@amazonia.food" className="text-sm text-[#00A5FF] hover:underline">info@amazonia.food</a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2 border border-black/10 p-8">
            {sent ? (
              <div className="text-center py-12">
                <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#111] mb-2">Message envoyé !</h3>
                <p className="text-sm text-black/60">Nous vous répondrons dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Nom *</label>
                    <input
                      type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                      data-testid="contact-name"
                      className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Email *</label>
                    <input
                      type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                      data-testid="contact-email"
                      className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Téléphone</label>
                  <input
                    type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                    data-testid="contact-phone"
                    className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Message *</label>
                  <textarea
                    required rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
                    data-testid="contact-message"
                    className="w-full px-3 py-2.5 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none resize-none transition-shadow duration-200"
                  />
                </div>
                {error && <p className="text-sm text-[#E8231B]">{error}</p>}
                <button
                  type="submit" disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
                >
                  <Send size={14} />
                  {loading ? 'Envoi...' : 'Envoyer le message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
